import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FaqArticlePayload,
  FaqArticleReorderPayload,
  FaqArticleStatus,
  FaqCategoryListParams,
} from 'Modules'

import { AppAdmin } from 'api/app-admin'
import { revalidateWebUser } from 'utils/revalidate-web-user'

import { applyArticleFields, FaqCategoryListCache } from './faq-list-optimistic'
import { faqArticleKeys, faqCategoryKeys } from './query-keys'

const invalidateList = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: faqCategoryKeys.lists() })
}

export const useCreateFaqArticle = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: FaqArticlePayload) => {
      const res = await AppAdmin.createFaqArticle(payload)
      return res.data?.data
    },
    onSuccess: () => {
      invalidateList(queryClient)
      revalidateWebUser(['faq'])
    },
  })
}

export const useUpdateFaqArticle = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: FaqArticlePayload }) => {
      const res = await AppAdmin.updateFaqArticle(id, payload)
      return res.data?.data
    },
    onSuccess: (data, { id }) => {
      invalidateList(queryClient)
      revalidateWebUser(['faq'])
      if (data) {
        queryClient.setQueryData(faqArticleKeys.detail(id), data)
      }
    },
  })
}

export const useUpdateFaqArticleStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: FaqArticleStatus }) => {
      const res = await AppAdmin.updateFaqArticleStatus(id, { status })
      return res.data?.data
    },
    onSuccess: (data, { id }) => {
      invalidateList(queryClient)
      revalidateWebUser(['faq'])
      if (data) {
        queryClient.setQueryData(faqArticleKeys.detail(id), data)
      }
    },
  })
}

type ReorderArticleVariables = {
  id: string
  payload: FaqArticleReorderPayload
  nextResults?: FaqCategoryListCache['results']
}

export const useReorderFaqArticle = (listParams: FaqCategoryListParams = {}) => {
  const queryClient = useQueryClient()
  const queryKey = faqCategoryKeys.list(listParams)

  return useMutation({
    mutationFn: async ({ id, payload }: ReorderArticleVariables) => {
      const res = await AppAdmin.reorderFaqArticle(id, payload)
      return res.data?.data
    },
    onMutate: async ({ nextResults }) => {
      if (!nextResults) {
        return {}
      }
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<FaqCategoryListCache>(queryKey)
      queryClient.setQueryData<FaqCategoryListCache>(queryKey, (old) =>
        old ? { ...old, results: nextResults } : old,
      )
      return { previous }
    },
    onSuccess: (data) => {
      revalidateWebUser(['faq'])
      if (!data?.article) {
        return
      }
      queryClient.setQueryData<FaqCategoryListCache>(queryKey, (old) =>
        old ? { ...old, results: applyArticleFields(old.results, data.article) } : old
      )
      queryClient.invalidateQueries({ queryKey: faqArticleKeys.detail(data.article._id) })
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      void queryClient.invalidateQueries({ queryKey })
    },
  })
}

export const useDeleteFaqArticle = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await AppAdmin.deleteFaqArticle(id)
    },
    onSuccess: (_data, id) => {
      invalidateList(queryClient)
      revalidateWebUser(['faq'])
      queryClient.removeQueries({ queryKey: faqArticleKeys.detail(id) })
    },
  })
}
